import React, { FC, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { PartsProduct, KINTEKI, FULL } from '@app/react/const/type';
import { ASSETS_PATH, NO_IMAGE_PRODUCT_PATH } from '@app/react/const/variables';
import { ZoomDialogContainer } from '@container/Dialog';
import { useOrderPathParams } from '@app/react/contexts/useOrderPathParams';
import { useMediaContext } from '@app/react/contexts/useMediaContext';
import { ZoomButton } from './ProductThumbnailZoomButton';

const defaultRender: FC<PartsProduct> = ({ name }) => (
    <>
        {`${name}`}
    </>
);

const defaultPriceRender: FC<PartsProduct> = ({ price }) => (
    <>
        {`(+¥${price.toLocaleString()}/本)`}
    </>
);

export type OnSelectProductHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
    product: PartsProduct
) => void;

export interface ProductThumbnailProps {
    className?: string;
    product: PartsProduct;
    checked: boolean;
    groupName: string;
    onClick: OnSelectProductHandler;
    disabled?: boolean;
    onNameRender?: (item: PartsProduct) => React.ReactNode;
    onPriceRender?: (item: PartsProduct) => React.ReactNode;
}

export const ProductThumbnail4Desktop: FC<ProductThumbnailProps> = (props) => {
    const {
        product,
        checked,
        groupName,
        onClick,
        disabled = false,
        onNameRender = defaultRender,
        onPriceRender = defaultPriceRender,
    } = props;

    const { arrowType, orderType } = useOrderPathParams();
    const [isShow, setisshow] = useState<boolean>(false);

    const { name } = product;
    const imgSrc = product.productImages[0] || NO_IMAGE_PRODUCT_PATH;
    const familyCrestEnable = (() => {
        const opt = product.options?.familyCrestEnable ?? false;
        if (arrowType === KINTEKI && orderType === FULL) {
            return opt;
        }
        return false;
    })();

    const bodyClassName = classNames('body', {
        disabled,
        selected: checked,
    });

    /* eslint-disable jsx-a11y/label-has-associated-control,
       jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    */
    return (
        <li className="product-thumbnail item">
            <label className={bodyClassName}>
                <input
                    type="radio"
                    name={groupName}
                    onChange={(ev) => onClick(ev, product)}
                    checked={checked}
                    disabled={disabled}
                />
                <div className="product-thumbnail__thumbnail">
                    <img
                        src={imgSrc}
                        alt={name}
                    />
                    {familyCrestEnable && <span className="familycrest-enabled">家紋印刷可能</span>}
                </div>
                <div className="product-thumbnail__name">
                    {onNameRender(product)}
                    <br />
                    {onPriceRender(product)}
                </div>
                <div className="product-thumbnail__icon-selected">
                    <img
                        src={`${ASSETS_PATH}/img/common/icon-selected.svg`}
                        alt="選択中"
                    />
                </div>
                <ZoomButton onClick={() => { setisshow(true); }} />
            </label>
            <ZoomDialogContainer
                isShow={isShow}
                setisshow={setisshow}
                product={product}
            />
        </li>
    );
    /* eslint-enable jsx-a11y/label-has-associated-control,
       jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
     */
};

export const ProductThumbnail4Mobile: FC<ProductThumbnailProps> = ({
    className,
    product,
    checked,
    groupName,
    onClick,
    disabled = false,
    onNameRender = defaultRender,
    onPriceRender = defaultPriceRender,
}) => {
    const { arrowType, orderType } = useOrderPathParams();
    const [isShow, setisshow] = useState<boolean>(false);

    const { name } = product;
    const imgSrc = product.productImages[0] || NO_IMAGE_PRODUCT_PATH;
    const familyCrestEnable = (() => {
        const opt = product.options?.familyCrestEnable ?? false;
        if (arrowType === KINTEKI && orderType === FULL) {
            return opt;
        }
        return false;
    })();

    const bodyClassName = classNames('body', {
        disabled,
        selected: checked,
    });

    /* eslint-disable jsx-a11y/label-has-associated-control,
       jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions,
       jsx-a11y/no-noninteractive-element-interactions
    */
    return (
        <li className={classNames('product-thumbnail item', className)}>
            <label className={bodyClassName}>
                <input
                    type="radio"
                    name={groupName}
                    checked={checked}
                    disabled={disabled}
                    onChange={() => {}}
                />
                <div className="product-thumbnail__thumbnail">
                    <img
                        src={imgSrc}
                        alt={name}
                        onClick={(ev) => {
                            if (!disabled && !checked) {
                                onClick(ev, product);
                            }
                        }}
                    />
                    {familyCrestEnable && <span className="familycrest-enabled">家紋印刷可能</span>}
                </div>
                <button type="button" className="footer" onClick={() => { setisshow(true); }}>
                    <div className="product-thumbnail__name">
                        {onNameRender(product)}
                    </div>
                    <div className="product-thumbnail__name">
                        {onPriceRender(product)}
                    </div>
                    <FontAwesomeIcon
                        className="product-thumbnail__icon-zoom"
                        icon={faEllipsisV}
                        color="#676767"
                    />
                </button>
                <div className="product-thumbnail__icon-selected">
                    <img
                        src={`${ASSETS_PATH}/img/common/icon-selected.svg`}
                        alt="選択中"
                    />
                </div>
            </label>
            <ZoomDialogContainer
                isShow={isShow}
                setisshow={setisshow}
                product={product}
            />
        </li>
    );
    /* eslint-enable jsx-a11y/label-has-associated-control,
       jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions,
       jsx-a11y/no-noninteractive-element-interactions
     */
};

export const ProductThumbnail: FC<ProductThumbnailProps> = (props) => {
    const { desktop } = useMediaContext();

    if (desktop) {
        return (<ProductThumbnail4Desktop {...props} />);
    }

    return (<ProductThumbnail4Mobile {...props} />);
};
